import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { getCurrencyByRegion } from '@principle-theorem/accounting';
import {
  CurrentPatientScope,
  GlobalStoreService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  SelectionListStore,
  TrackByFunctions,
} from '@principle-theorem/ng-shared';
import {
  ChartedTreatment,
  Patient,
  VariablePricingProvider,
  getServiceCodeItems,
  stafferToNamedDoc,
} from '@principle-theorem/principle-core';
import {
  type AnyChartedItemConfiguration,
  type ChartableSurface,
  ChartedItemType,
  type IAppointment,
  type IChartedItem,
  type IChartedItemSelectionSummary,
  type IChartedMultiStepTreatment,
  type IChartedMultiStepTreatmentStep,
  type IChartedRef,
  type IChartedServiceSmartGroup,
  type IChartedTreatment,
  type IChartedTreatmentGroup,
  type IDepositMetadata,
  type IFeeSchedule,
  type IPatient,
  type IPricedServiceCodeEntry,
  IPricedServiceCodeGroup,
  IPricingRule,
  IServiceCode,
  type IServiceCodeEntry,
  type IStaffer,
  type ITreatmentCategory,
  type ITreatmentConfiguration,
  type ITreatmentPackage,
  type ITreatmentPlan,
  type ITreatmentStep,
  ServiceCodeGroupType,
  TreatmentFeeMode,
  isChartedTreatment,
  isMohAnnualConsultation,
} from '@principle-theorem/principle-core/interfaces';
import {
  type DocumentReference,
  type WithRef,
  filterUndefined,
  getDoc,
  multiMap,
  reduce2DArray,
  shareReplayCold,
  snapshot,
} from '@principle-theorem/shared';
import { isEqual, isUndefined, sortBy, uniq } from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  type Observable,
  ReplaySubject,
  of,
} from 'rxjs';
import {
  distinctUntilKeyChanged,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { type IEditChartableData } from '../../chartable-surface-updater';
import { ChartedItemActionsService } from '../../services/charted-item-actions.service';
import { ChartedTreatmentDeposit } from './charted-treatment-deposit';

@Component({
  selector: 'pr-treatment-scope',
  templateUrl: './treatment-scope.component.html',
  styleUrls: ['./treatment-scope.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [ChartedItemActionsService],
})
export class TreatmentScopeComponent {
  trackByTreatments = TrackByFunctions.uniqueId<IChartedTreatment>();
  treatment$ = new ReplaySubject<IChartedTreatment>(1);
  step$ = new BehaviorSubject<
    WithRef<ITreatmentStep> | IChartedMultiStepTreatmentStep | undefined
  >(undefined);
  plan$ = new BehaviorSubject<
    WithRef<ITreatmentPlan> | IChartedMultiStepTreatment | undefined
  >(undefined);
  config$: Observable<WithRef<ITreatmentConfiguration>>;
  @Input() expanded = false;
  @Input() disabled = false;
  @Input() compact = false;
  isNested = input<boolean>(false);
  appointment = input<WithRef<IAppointment>>();
  @Output() treatmentChanged = new EventEmitter<IChartedTreatment>();
  @Output() treatmentDeleted = new EventEmitter<IChartedTreatment>();
  @Output() updateChartable = new EventEmitter<IEditChartableData>();
  @Output() categoryChanged = new EventEmitter<WithRef<ITreatmentCategory>>();
  @Output() serviceCodeChanged = new EventEmitter<IServiceCode>();
  selectedSurfaces$ = new ReplaySubject<Partial<IChartedRef>[]>(1);
  feeSchedule$: Observable<WithRef<IFeeSchedule>>;
  serviceCodeSummary$: Observable<string>;
  clearTooltip$: Observable<string>;
  treatmentCategory$: Observable<
    DocumentReference<ITreatmentCategory> | undefined
  >;
  availableSurfaces$: Observable<ChartableSurface[]>;
  treatmentPackages$: Observable<ITreatmentPackage[]>;
  patient$: Observable<WithRef<IPatient>>;
  commonCodes$: Observable<IServiceCodeEntry[]>;
  treatmentType = ChartedItemType;
  deposit$: Observable<IDepositMetadata | undefined>;

  @Input()
  set treatment(treatment: IChartedTreatment) {
    if (treatment) {
      this.treatment$.next(treatment);
    }
  }

  @Input()
  set selectedSurfaces(selectedSurfaces: Partial<IChartedRef>[]) {
    if (selectedSurfaces) {
      this.selectedSurfaces$.next(selectedSurfaces);
    }
  }

  @Input()
  set step(step: WithRef<ITreatmentStep> | IChartedMultiStepTreatmentStep) {
    if (step) {
      this.step$.next(step);
    }
  }

  @Input()
  set plan(plan: WithRef<ITreatmentPlan> | IChartedMultiStepTreatment) {
    if (plan) {
      this.plan$.next(plan);
    }
  }

  constructor(
    private _globalStore: GlobalStoreService,
    private _patientScope: CurrentPatientScope,
    private _chartedItemActions: ChartedItemActionsService,
    public elementRef: ElementRef,
    public organisation: OrganisationService,
    @Optional()
    public selectionList?: SelectionListStore<
      IChartedItemSelectionSummary<IChartedItem>
    >
  ) {
    this.patient$ = this._patientScope.doc$.pipe(filterUndefined());

    this.feeSchedule$ = this.treatment$.pipe(
      switchMap((treatment) =>
        this._globalStore.getFeeSchedule$(treatment.feeSchedule.ref)
      )
    );

    this.serviceCodeSummary$ = this.treatment$.pipe(
      withLatestFrom(this.organisation.taxRate$.pipe(filterUndefined())),
      map(([treatment, taxRate]) => getServiceCodeItems(treatment, taxRate)),
      multiMap((serviceCode) => serviceCode.code),
      map((serviceCodes) => sortBy(serviceCodes).join(', '))
    );

    this.config$ = this.treatment$.pipe(
      switchMap((treatment) => getDoc(treatment.config.ref)),
      shareReplayCold()
    );

    this.treatmentPackages$ = this.config$.pipe(
      map((config) => config.packages)
    );

    this.availableSurfaces$ = this.config$.pipe(
      map((config) => config.availableSurfaces)
    );

    this.commonCodes$ = this.config$.pipe(
      map((config) =>
        config.serviceCodeGroups
          .filter((group) => group.type === ServiceCodeGroupType.Optional)
          .map((group) => group.serviceCodes)
      ),
      reduce2DArray()
    );

    this.treatmentCategory$ = this.config$.pipe(
      map(({ category }) => category)
    );

    this.clearTooltip$ = this.treatment$.pipe(
      map((treatment) =>
        treatment.attributedTo
          ? `Clear ${treatment.attributedTo.name} as Treatment Provider`
          : ''
      )
    );

    this.deposit$ = combineLatest([
      this.treatment$,
      this.step$,
      this.organisation.brand$,
      toObservable(this.appointment),
      this.organisation.organisation$.pipe(filterUndefined()),
    ]).pipe(
      map(([treatment, step, brand, appointment, currentOrganisation]) =>
        ChartedTreatmentDeposit.resolve(
          treatment,
          step?.treatments ?? [treatment],
          brand?.settings?.onlineBookings?.deposit,
          appointment,
          getCurrencyByRegion(currentOrganisation.region)
        )
      )
    );

    this.treatment$
      .pipe(
        distinctUntilKeyChanged('uuid'),
        filter(isChartedTreatment),
        withLatestFrom(this.patient$),
        takeUntilDestroyed()
      )
      .subscribe(([treatment, patient]) =>
        this._setMohConsulationCapitatedFee(treatment, patient)
      );
  }

  async deleteTreatment(treatment: IChartedTreatment): Promise<void> {
    const confirmed = await this._chartedItemActions.confirmDelete(treatment);
    if (!confirmed) {
      return;
    }
    this.treatmentDeleted.emit(treatment);
  }

  treatmentChange(treatment: IChartedTreatment): void {
    this.treatmentChanged.emit(treatment);
  }

  updateSmartGroups(
    treatment: IChartedTreatment,
    serviceCodeSmartGroups: IChartedServiceSmartGroup[]
  ): void {
    this.treatmentChange({
      ...treatment,
      serviceCodeSmartGroups,
    });
  }

  updateExclusiveGroups(
    serviceCodeGroups: IPricedServiceCodeGroup[],
    treatment: IChartedTreatment
  ): void {
    this.treatmentChange({
      ...treatment,
      serviceCodeGroups,
    });
  }

  updateServiceCodes(
    treatment: IChartedTreatment,
    serviceCodes: IPricedServiceCodeEntry[]
  ): void {
    this.treatmentChange({
      ...treatment,
      serviceCodes,
    });
  }

  async updateBasePricePricingRule(pricingRule: IPricingRule): Promise<void> {
    const treatment = await snapshot(this.treatment$);
    this.treatmentChange({
      ...treatment,
      basePriceOverride: undefined,
      basePricePricingRule: pricingRule,
    });
  }

  async updateBasePriceOverride(
    basePriceOverride: number | undefined
  ): Promise<void> {
    const treatment = await snapshot(this.treatment$);
    this.treatmentChange({
      ...treatment,
      basePriceOverride,
    });
  }

  async resetToFeeSchedule(): Promise<void> {
    const treatment = await snapshot(this.treatment$);
    this.treatmentChange({
      ...treatment,
      basePriceOverride: undefined,
    });
  }

  async addBasePrice(treatment: IChartedTreatment): Promise<void> {
    const newBasePrice = await this._chartedItemActions.addBasePrice(
      ChartedTreatment.getBasePrice(treatment)
    );
    if (isUndefined(newBasePrice)) {
      return;
    }

    this.treatmentChange({
      ...treatment,
      basePriceOverride: newBasePrice,
    });
  }

  async hasBasePrice$(treatment: IChartedTreatment): Promise<boolean> {
    const feeSchedule = await snapshot(this.feeSchedule$);
    if (feeSchedule.treatmentFeeMode === TreatmentFeeMode.FixedTotal) {
      return true;
    }
    const basePrice = ChartedTreatment.getBasePrice(treatment);
    return basePrice !== 0 || !!treatment.basePricePricingRule;
  }

  deleteBasePrice(treatment: IChartedTreatment): void {
    this.treatmentChange({
      ...treatment,
      basePriceOverride: 0,
      basePricePricingRule: undefined,
    });
  }

  async editSurfaces(item: IChartedTreatment): Promise<void> {
    const chartable = await snapshot(this.config$);
    const plan = await snapshot(this.plan$);
    const step = await snapshot(this.step$);

    const editData = await this._chartedItemActions.editSurfaces(
      chartable,
      item,
      plan,
      step
    );

    if (!editData) {
      return;
    }

    this.updateChartable.emit(editData);
  }

  async setFeeSchedule(feeSchedule: WithRef<IFeeSchedule>): Promise<void> {
    const treatment = await snapshot(this.treatment$);
    const step = await snapshot(this.step$);

    const updatedTreatment = await this._chartedItemActions.applyFeeSchedule(
      treatment,
      feeSchedule,
      step?.treatments ?? []
    );

    this.treatmentChange(updatedTreatment as IChartedTreatment);
  }

  async setAttributedTo(staffer: WithRef<IStaffer> | undefined): Promise<void> {
    const treatment = await snapshot(this.treatment$);
    const attributedTo = staffer ? stafferToNamedDoc(staffer) : undefined;

    this.treatmentChange({
      ...treatment,
      attributedTo,
    });
  }

  isSelected$(
    item: IChartedItem<AnyChartedItemConfiguration>
  ): Observable<boolean> {
    return this.step$.pipe(
      switchMap(
        (step) =>
          this.selectionList?.isSelected$({
            item,
            step,
          }) ?? of(false)
      )
    );
  }

  isCheckboxDisabled$(
    item: IChartedTreatment | IChartedTreatmentGroup
  ): Observable<boolean> {
    return (this.selectionList?.selected$ || of([])).pipe(
      map((selected) => {
        if (!selected.length) {
          return false;
        }

        const itemTypes = uniq(
          selected.map((selectedItem) => selectedItem.item.type)
        );

        if (
          item.type === ChartedItemType.ChartedTreatmentGroup &&
          itemTypes.includes(ChartedItemType.ChartedTreatment)
        ) {
          return false;
        }

        return !itemTypes.includes(item.type);
      })
    );
  }

  async setSelected(
    item: IChartedTreatment | IChartedTreatmentGroup,
    isSelected: boolean
  ): Promise<void> {
    const step = await snapshot(this.step$);
    const plan = await snapshot(this.plan$);

    this.selectionList?.setSelected(
      {
        item,
        step,
        plan,
      },
      isSelected
    );
  }

  setTreatmentPackage(
    selectedPackageUid: string | undefined,
    packages: ITreatmentPackage[],
    treatment: IChartedTreatment
  ): void {
    if (!selectedPackageUid) {
      this.treatmentChange(ChartedTreatment.clearPackagePricing(treatment));
      return;
    }

    const selectedPackage = packages.find(
      (treatmentPackage) => treatmentPackage.uid === selectedPackageUid
    );

    if (!selectedPackage) {
      return;
    }

    this.treatmentChange(
      ChartedTreatment.applyPackagePricing(treatment, selectedPackage)
    );
  }

  getStepTreatments$(
    step$: Observable<
      WithRef<ITreatmentStep> | IChartedMultiStepTreatmentStep | undefined
    >
  ): Observable<IChartedTreatment[]> {
    return step$.pipe(map((step) => step?.treatments ?? []));
  }

  private _setMohConsulationCapitatedFee(
    treatment: IChartedTreatment,
    patient: WithRef<IPatient>
  ): void {
    const capitationCode = Patient.getMohCapitationBand(patient).code;

    const serviceCodes = treatment.serviceCodes.map((serviceCode) => {
      if (!isMohAnnualConsultation(serviceCode)) {
        return serviceCode;
      }

      const pricingProvider = new VariablePricingProvider();
      const pricingRule = pricingProvider.selectByLabel(
        serviceCode.pricingRule,
        capitationCode
      );
      const price =
        pricingProvider.determinePrice(pricingRule) ?? serviceCode.price;
      return {
        ...serviceCode,
        pricingRule,
        price,
      };
    });

    if (!isEqual(serviceCodes, treatment.serviceCodes)) {
      this.updateServiceCodes(treatment, serviceCodes);
    }
  }
}
